import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";

/**
 * Generic list editor for services / products / team / jobs.
 * fields: [{ key, label, type: 'text'|'textarea'|'list'|'bool'|'number', placeholder? }]
 */
export default function CrudEditor({ title, subtitle, endpoint, fields, emptyItem, testKey }) {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null); // item id or 'new'
  const [form, setForm] = React.useState(emptyItem);
  const [loading, setLoading] = React.useState(false);

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  React.useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...emptyItem }); setEditing("new"); };
  const startEdit = (it) => { setForm({ ...it }); setEditing(it.id); };
  const close = () => { setEditing(null); setForm(emptyItem); };

  const save = async () => {
    setLoading(true);
    try {
      if (editing === "new") await api.post(endpoint, form);
      else await api.put(`${endpoint}/${editing}`, form);
      toast.success("Saved");
      close(); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[color:var(--sky-cyan)]">Manage</div>
          <h1 className="mt-3 font-display font-black text-white text-3xl">{title}</h1>
          {subtitle && <p className="mt-2 text-[color:var(--sky-admin-muted)]">{subtitle}</p>}
        </div>
        <button data-testid={`${testKey}-add`} onClick={startNew} className="admin-btn inline-flex items-center gap-2"><Plus size={14} /> Add new</button>
      </div>

      <div className="mt-8 grid gap-3">
        {items.map((it) => (
          <div key={it.id} className="admin-card p-5 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-white truncate">{it.name || it.title}</div>
              <div className="text-xs text-[color:var(--sky-admin-muted)] mt-1 truncate">{it.tagline || it.role || it.department || it.category || ""}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`${testKey}-edit-${it.id}`} onClick={() => startEdit(it)} className="admin-btn-ghost text-xs">Edit</button>
              <button data-testid={`${testKey}-delete-${it.id}`} onClick={() => remove(it.id)} className="admin-btn-ghost text-xs text-red-300 hover:text-red-200"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="admin-card p-8 text-center text-[color:var(--sky-admin-muted)]">No items yet. Click &ldquo;Add new&rdquo; to start.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4">
          <div className="admin-card w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-5 border-b border-[color:var(--sky-admin-border)] flex items-center justify-between">
              <div className="font-display font-bold text-white text-lg">{editing === "new" ? `New ${title.slice(0, -1)}` : `Edit ${title.slice(0, -1)}`}</div>
              <button onClick={close} className="text-[color:var(--sky-admin-muted)] hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-6 grid gap-4">
              {fields.map((f) => (
                <div key={f.key} className="grid gap-1">
                  <label className="text-xs uppercase tracking-widest text-[color:var(--sky-admin-muted)] font-bold">{f.label}</label>
                  {f.type === "textarea" && <textarea rows={4} className="admin-input" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} />}
                  {f.type === "text" && <input className="admin-input" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} />}
                  {f.type === "number" && <input type="number" className="admin-input" value={form[f.key] ?? 0} onChange={(e) => setForm({ ...form, [f.key]: Number(e.target.value) })} />}
                  {f.type === "bool" && (
                    <label className="inline-flex items-center gap-2 text-sm text-white">
                      <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} />
                      Enabled
                    </label>
                  )}
                  {f.type === "list" && (
                    <textarea rows={4} className="admin-input font-mono-plex text-xs" placeholder="One item per line" value={(form[f.key] || []).join("\n")} onChange={(e) => setForm({ ...form, [f.key]: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
                  )}
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[color:var(--sky-admin-border)] flex items-center justify-end gap-3">
              <button onClick={close} className="admin-btn-ghost text-sm">Cancel</button>
              <button data-testid={`${testKey}-save`} disabled={loading} onClick={save} className="admin-btn inline-flex items-center gap-2 disabled:opacity-60"><Save size={14} /> {loading ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
